import { CommonModule, DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { Observable, Subscription } from 'rxjs';
import { Role } from '../../enums/role';
import { Service } from '../../enums/service';
import { Department } from '../../models/department';
import { ServiceConfiguration } from '../../models/service-configuration';
import { ApiService } from '../../services/api.service';
import { UiNotificationService } from '../../services/ui-notification.service';
import { Functions } from '../../utilities/functions';
import { Parameters } from '../../utilities/parameters';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { Reset } from '../../utilities/reset';

@Component({
  selector: 'app-department-dialog',
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    TabsModule,
    SelectButtonModule,
    InputNumberModule,
    ButtonModule,
    DividerModule,
    TranslatePipe,
    TooltipModule,
    ToggleSwitchModule
  ],
  providers: [DatePipe],
  templateUrl: './department-dialog.component.html',
  styles: ``,
})
export class DepartmentDialogComponent implements OnInit, OnDestroy {
  private translate = inject(TranslateService);
  private langSubscription!: Subscription

  @Input() isNew = false;
  @Input() messageService!: UiNotificationService;

  // visibilità dialog (bidirezionale)
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  @Input() department: Department = Reset.department();

  // evento finale
  @Output() saved = new EventEmitter<Department>();

  tabsId: string = '0';
  isSuperAdmin: boolean = false;

  organizationOptions: { id: number; value: string }[] = [];
  selectedOrganization: { id: number; value: string } = { id: 0, value: '' };

  activeOptions: any[] = [{ label: 'On', value: true }, { label: 'Off', value: false }];
  type: typeof Service = Service;

  selectedService: ServiceConfiguration = Reset.serviceConfiguration();
  modeOptions: any[] = [];
  useLimit: boolean = true;

  showConfiguration: boolean = false;

  constructor(private apiService: ApiService, private datePipe: DatePipe, private cdr: ChangeDetectorRef) {
    this.isSuperAdmin = Parameters.loggedUser.roles.includes(Role.superAdmin);
  }

  ngOnChanges() {
    this.tabsId = '0';

    if (this.department && this.visible) {
      if (!this.isNew) {
        this.apiService.getDepartmentServices(this.department.organizationId, this.department.departmentId).subscribe({
          next: (s) => {
            this.department.features = s;

            const orgId_selectedDepartment = this.organizationOptions.find(org => org.id === this.department.organizationId);
            if (orgId_selectedDepartment)
              this.selectedOrganization = orgId_selectedDepartment;

            this.cdr.detectChanges();
          },
          error: (err: HttpErrorResponse) => {
            this.messageService.showError(err);
          }
        })
      }
    }
  }

  ngOnDestroy() {
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
  }

  ngOnInit(): void {
    this.langSubscription = this.translate.stream(['months', 'executions']).subscribe(translations => {
      this.modeOptions = [
        { label: translations['executions'], value: true },
        { label: translations['months'], value: false }
      ];
    });

    // questo avviene solo la prima volta che viene aperta la dialog
    if (this.isSuperAdmin) {
      // superAdmin deve poter vedere tutte le organizzazioni
      this.apiService.getOrganizations().subscribe({
        next: (data) => {
          this.organizationOptions = data.map(org => ({
            id: org.organizationId,
            value: org.name
          }));
        },
        error: (err: HttpErrorResponse) => {
          this.messageService.showError(err);
        }
      })
    } else if ({ ...Parameters.loggedUser }.roles.includes(Role.admin)) {
      // a partire dalle organizzazioni associate all'utente crea le opzioni per un filtro da usare in una tabella a selezione multipla
      this.organizationOptions = { ...Parameters.loggedUser }.organizations.map(org => ({
        id: org.organizationId,
        value: org.name
      }));
    }
  }

  checkIsActive(type: Service): boolean {
    if (this.department.features === null) {
      return false;
    }

    if (this.department.features.length === 0) {
      return false;
    }

    var feature = this.department.features.find(f => f.serviceId === type);
    return feature !== undefined ? feature.isActive : false;
  }

  getServiceInfo(type: Service): Observable<string> {
    var sc: ServiceConfiguration | undefined = this.department.features.find(f => f.serviceId === type);
    if (sc === undefined) {
      return this.translate.get('functions.notDefined');
    }

    if (sc.expirationAt === new Date(0)) {
      return this.translate.get('functions.toConfigure');
    }

    if (sc.limit !== 0) {
      if (sc.autoRenewal) {
        return this.translate.get('functions.executionAuto', {
          occurrencies: sc.occurrencies,
          limit: sc.limit
        });
      } else {
        return this.translate.get('functions.executionManual', {
          remaining: sc.limit - sc.occurrencies
        });
      }
    } else {
      if (sc.autoRenewal) {
        return this.translate.get('functions.monthly', {
          months: sc.expirationMonths
        });
      } else {
        const formattedDate = this.datePipe.transform(sc.expirationAt, 'mediumDate') || '';
        return this.translate.get('functions.expiration', {
          date: formattedDate
        });
      }
    }
  }

  getServiceName(id: number): string {
    return Functions.getServiceName(id);
  }

  onClose() {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  onModeSelection(): void {
    this.useLimit = !this.useLimit;

    if (this.useLimit) {
      this.selectedService.expirationMonths = 1;
    } else {
      this.selectedService.limit = 0;
    }
  }

  onSave() {
    if (this.isNew) {
      this.department.organizationId = this.selectedOrganization.id;
      this.apiService.addDepartment(this.department).subscribe({
        next: () => {
          this.savedCompleted();
          this.messageService.showSuccess('messages.dataSaved');
        },
        error: (err: HttpErrorResponse) => {
          this.messageService.showError(err);
        }
      });
    } else {
      this.apiService.updateDepartment(this.department).subscribe({
        next: () => {
          this.savedCompleted();
          this.messageService.showSuccess('messages.dataSaved');
        },
        error: (err: HttpErrorResponse) => {
          this.messageService.showError(err);
        }
      });
    }
  }

  onServiceSelection(type: Service, event: any): void {
    var sc: ServiceConfiguration | undefined = this.department.features.find(f => f.serviceId === type);
    if (sc !== undefined) {
      sc.isActive = event.option.value;
    } else {
      var item: ServiceConfiguration = Reset.serviceConfiguration();
      item.serviceId = type;
      item.organizationId = this.department.organizationId;
      item.departmentId = this.department.departmentId;
      item.isActive = event.option.value;

      this.department.features.push(item);
    }
  }

  onShowConfiguration(type: Service): void {
    var sc: ServiceConfiguration | undefined = this.department.features.find(f => f.serviceId === type);
    if (sc === undefined) {
      this.messageService.showErrorMessage('messages.serviceNotFound');
    } else {
      this.selectedService = { ...sc }
      this.useLimit = sc.limit !== 0;
      this.showConfiguration = true;
      this.cdr.detectChanges();
    }
  }

  onUpdateConfiguration(): void {
    var sc: ServiceConfiguration | undefined = this.department.features.find(f => f.serviceId === this.selectedService.serviceId);
    if (sc === undefined) {
      this.messageService.showErrorMessage('messages.serviceNotFound');
    } else {
      sc.limit = this.selectedService.limit;
      sc.occurrencies = this.selectedService.occurrencies;
      sc.expirationMonths = this.selectedService.expirationMonths;
      sc.autoRenewal = this.selectedService.autoRenewal;
    }

    this.showConfiguration = false;
  }

  private savedCompleted() {
    this.saved.emit(this.department);
    this.onClose();
  }
}
